/**
 * applyUiDirective — the ui-action `report.*` / `html.*` / `artifact.*` switch.
 *
 * Sub-appliers preserve EXACT else-if precedence: report runs first, then html,
 * then artifact. Each returns true when it handled the action; an unrecognised
 * action falls through all three (no-op). An AbortError thrown mid-create inside
 * a sub-applier propagates out unchanged so runChatStream's try/catch performs
 * orphan cleanup.
 *
 * Mutates the shared {@link TurnContext} BY REFERENCE — never destructure or copy
 * ctx fields. Pinned by useChat.ui-directive-char.test.ts +
 * useChat.html-stream-char.test.ts.
 */

import { applyReportDirective } from './applyReportDirective'
import { applyHtmlDirective } from './applyHtmlDirective'
import { applyArtifactDirective } from './applyArtifactDirective'
import type { TurnContext, ChatStreamDeps } from './useChatStream.types'

type UiAction = { type: string; [key: string]: unknown }

export async function applyUiDirective(
  ctx: TurnContext,
  action: UiAction,
  deps: ChatStreamDeps,
  signal: AbortSignal,
): Promise<void> {
  const handledByReport = await applyReportDirective(ctx, action, deps, signal)
  if (handledByReport) return
  const handledByHtml = await applyHtmlDirective(ctx, action, deps, signal)
  if (handledByHtml) return
  const handledByArtifact = await applyArtifactDirective(ctx, action, deps, signal)
  if (handledByArtifact) return
}
